import React from "react";
import cc from "classcat";
import { useLocalization } from "@fluent/react";
import { Checkbox, Radio, Tooltip } from "@narmi/design_system";
import {
  AccountPermissionGroup,
  AccountPermissionOption,
  AccountPermissionParent,
  getAvailableAccountPermissions,
} from "byzantine/src/Role";
import getAccountPermissionLabel, {
  getAccountPermissionParentLabel,
} from "./accountPermissionOptions";
import {
  AccountPermissionState,
  LOAN_ACCOUNT_PERMISSIONS,
} from "./RoleFormUtils";
import styles from "./OrganizationRoles.module.scss";

const GROUPS: AccountPermissionGroup[] = ["moneyMovement", "manageTemplates"];

interface AccountPermissionsEditorProps {
  accountUuid: string;
  value: AccountPermissionState;
  onChange: (next: AccountPermissionState) => void;
  features?: Record<string, any>;
  /** A loan account only offers transfers, so the other permissions are left out. */
  isLoan?: boolean;
}

/** Expanded body of an account card: write or read access, then the account-level permissions. */
export default function AccountPermissionsEditor({
  accountUuid,
  value,
  onChange,
  features = {},
  isLoan = false,
}: AccountPermissionsEditorProps) {
  const { l10n } = useLocalization();
  const availableForInstitution = getAvailableAccountPermissions(features);
  const available = isLoan
    ? {
        moneyMovement: availableForInstitution.moneyMovement.filter((option) =>
          LOAN_ACCOUNT_PERMISSIONS.includes(option.modelStr)
        ),
        manageTemplates: [],
      }
    : availableForInstitution;
  const radioName = `account-access-${accountUuid}`;

  // Switching to View only hides the checklist but keeps the selections, so switching back
  // restores them. View only carries no scoped permissions: they are left out when saving.
  const setAccess = (access: API.AccountAccess) =>
    onChange({ ...value, access });

  const togglePermission = (modelStr: string) =>
    onChange({
      ...value,
      permissions: value.permissions.includes(modelStr)
        ? value.permissions.filter((permission) => permission !== modelStr)
        : [...value.permissions, modelStr],
    });

  const isSelected = (modelStr: string) => value.permissions.includes(modelStr);

  // Nesting is purely visual: `permissions` stays a flat list. A parent is checked while any of
  // its children is. Checking it selects every child; clearing it (or the last child) collapses it.
  const toggleParent = (childModelStrs: string[]) => {
    const permissions = childModelStrs.some(isSelected)
      ? value.permissions.filter((p) => !childModelStrs.includes(p))
      : [...value.permissions, ...childModelStrs.filter((p) => !isSelected(p))];
    onChange({ ...value, permissions });
  };

  const renderCheckbox = (modelStr: string) => (
    <Checkbox
      key={modelStr}
      kind="condensed"
      label={getAccountPermissionLabel(modelStr, l10n)}
      checked={isSelected(modelStr)}
      onChange={() => togglePermission(modelStr)}
    />
  );

  const renderParent = (
    parent: AccountPermissionParent,
    children: AccountPermissionOption[]
  ) => {
    const childModelStrs = children.map((child) => child.modelStr);
    const hasSelectedChild = childModelStrs.some(isSelected);
    const parentCheckbox = (
      <Checkbox
        kind="condensed"
        label={getAccountPermissionParentLabel(parent, l10n)}
        checked={hasSelectedChild}
        onChange={() => toggleParent(childModelStrs)}
      />
    );

    // A lone child has nothing to nest, so it is just the parent's checkbox.
    if (children.length === 1) return <div key={parent}>{parentCheckbox}</div>;

    return (
      <div
        key={parent}
        className={styles.permissionParent}
        role="group"
        aria-label={getAccountPermissionParentLabel(parent, l10n)}
      >
        {parentCheckbox}
        {hasSelectedChild && (
          <div className={styles.nestedPermissions}>
            {childModelStrs.map(renderCheckbox)}
          </div>
        )}
      </div>
    );
  };

  // Options are in display order: a parent shows up where its first child is, and is rendered once.
  const renderOptions = (options: AccountPermissionOption[]) => {
    const renderedParents = new Set<AccountPermissionParent>();
    return options.map(({ modelStr, parent }) => {
      if (!parent) return renderCheckbox(modelStr);
      if (renderedParents.has(parent)) return null;
      renderedParents.add(parent);
      return renderParent(
        parent,
        options.filter((option) => option.parent === parent)
      );
    });
  };

  return (
    <div className={styles.accountPermissions}>
      <fieldset className={styles.accessOptions}>
        <Radio
          name={radioName}
          kind="input-card"
          value="move_money"
          checked={value.access === "move_money"}
          onCheck={() => setAccess("move_money")}
        >
          <h4 className="fontFamily--body fontSize--m fontWeight--semibold">
            {l10n.getString("org-role-account-access-move-money")}
          </h4>
        </Radio>
        <Radio
          name={radioName}
          kind="input-card"
          value="view_only"
          checked={value.access === "view_only"}
          onCheck={() => setAccess("view_only")}
        >
          <h4 className="fontFamily--body fontSize--m fontWeight--semibold alignChild--left--center">
            {l10n.getString("org-role-account-access-view-only")}
            <span className={styles.accessInfo}>
              <Tooltip
                text={l10n.getString("org-role-account-view-only-tooltip")}
              >
                <span className="narmi-icon-info" />
              </Tooltip>
            </span>
          </h4>
        </Radio>
      </fieldset>
      {value.access === "move_money" &&
        GROUPS.map(
          (group) =>
            available[group].length > 0 && (
              <div key={group} className={styles.permissionGroup}>
                <p
                  className={cc(["nds-subheader", styles.permissionGroupTitle])}
                >
                  {group === "moneyMovement"
                    ? l10n.getString("org-role-account-group-money-movement")
                    : l10n.getString("org-role-account-group-manage-templates")}
                </p>
                {renderOptions(available[group])}
              </div>
            )
        )}
    </div>
  );
}
